import { useMemo } from "react";
import useQuery from "hooks/useQuery";
import LoadVelocityProfileService from "services/LoadVelocityProfileService";
import ErrorLoggingService from "services/ErrorLoggingService";
import type { Variants } from "types";
import type { LoadVelocityProfileLite, LoadVelocityProfileParams } from "types/load-velocity-profile";
import type { UseQueryOptions } from "types/use-query";

/** `variants` is set from the query args. */
type DateRangeParams = Omit<LoadVelocityProfileParams, "variants">;

export const getAthleteLoadVelocityProfilesCacheKey = (
  athleteId: string,
  exerciseId: string,
  params: DateRangeParams,
  variants: Variants[] = []
) => ["LoadVelocityProfileService.getAll", athleteId, exerciseId, params, ...variants] as const;

interface GetAthleteLoadVelocityProfilesQueryArgs {
  athleteId?: string | null;
  exerciseId?: string | null;
  params: DateRangeParams;
  /** Every variant is requested at once; without variants only bilateral profiles are returned. */
  variants?: Variants[];
}

interface GetAthleteLoadVelocityProfiles {
  athleteId: string;
  exerciseId: string;
  params: DateRangeParams;
  variants: Variants[];
}

async function getAthleteLoadVelocityProfiles(
  args: GetAthleteLoadVelocityProfiles
): Promise<LoadVelocityProfileLite[]> {
  const { athleteId, exerciseId, params, variants } = args;

  try {
    return await LoadVelocityProfileService.getAll(athleteId, exerciseId, {
      ...params,
      variants: variants.length ? variants : undefined,
    });
  } catch (error) {
    ErrorLoggingService.captureException(error, "Failed to fetch athlete load velocity profiles");
    throw error;
  }
}

const EMPTY_FALLBACK_DATA: LoadVelocityProfileLite[] = [];

/**
 * Fetches the predicted 1RM range of each of an athlete's load velocity profiles for one exercise, every variant in a single request.
 */
function useGetAthleteLoadVelocityProfilesQuery(
  args: GetAthleteLoadVelocityProfilesQueryArgs,
  options?: UseQueryOptions
) {
  const { athleteId, exerciseId, params, variants } = args;
  const { enabled = true, ...swrOptions } = options ?? {};

  const key = useMemo(() => {
    if (!athleteId || !exerciseId || !enabled) return null;
    return getAthleteLoadVelocityProfilesCacheKey(athleteId, exerciseId, params, variants);
  }, [athleteId, enabled, exerciseId, params, variants]);

  return useQuery(
    key,
    ([, athleteId, exerciseId, params, ...variants]) =>
      getAthleteLoadVelocityProfiles({ athleteId, exerciseId, params, variants }),
    {
      fallbackData: EMPTY_FALLBACK_DATA,
      ...swrOptions,
    }
  );
}

export default useGetAthleteLoadVelocityProfilesQuery;
